import type { ForumLang } from "../constants/forumTranslations";

const BRAND_NAVY_LIGHT = "#2a2f55";

type PolicyType = "terms" | "refunds" | "privacy";

type Props = {
  open: boolean;
  onClose: () => void;
  policy: PolicyType | null;
  lang: ForumLang;
  onLangChange?: (lang: ForumLang) => void;
};

export function PolicyModal({ open, onClose, policy, lang, onLangChange }: Props) {
  if (!open || !policy) return null;

  const isEs = lang === "es";

  /** Top bar (header) title */
  const headerTitles: Record<PolicyType, { es: string; en: string }> = {
    terms: { es: "Términos de uso", en: "Terms of Use" },
    refunds: { es: "Política de Devoluciones", en: "Refund Policy" },
    privacy: { es: "Política de Privacidad", en: "Privacy Policy" },
  };

  /** Big title beneath (body) */
  const bodyTitles: Record<PolicyType, { es: string; en: string }> = {
    terms: { es: "Políticas de servicio", en: "Service Policies" },
    refunds: { es: "Política de Reembolsos", en: "Refund and Cancellation Policy" },
    privacy: { es: "Política de Privacidad", en: "Privacy Policy" },
  };

  const headerTitle = isEs ? headerTitles[policy].es : headerTitles[policy].en;
  const bodyTitle = isEs ? bodyTitles[policy].es : bodyTitles[policy].en;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="policy-modal-title"
    >
      <div
        className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header: light grey bar, title left, X right (no top bar) */}
        <div className="flex items-center justify-between gap-4 bg-gray-100 border-b border-gray-200 px-4 py-3 shrink-0">
          <h2 id="policy-modal-title" className="text-base font-semibold text-gray-700 truncate">
            {headerTitle}
          </h2>
          <div className="flex items-center gap-2">
            {onLangChange && (
              <div className="flex rounded border border-gray-200/80 bg-white/80 p-px" role="group" aria-label={isEs ? "Idioma" : "Language"}>
                <button
                  type="button"
                  onClick={() => onLangChange("es")}
                  className={`rounded-sm px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider transition-colors ${
                    lang === "es" ? "text-white" : "text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                  }`}
                  style={lang === "es" ? { backgroundColor: BRAND_NAVY_LIGHT } : undefined}
                >
                  ES
                </button>
                <button
                  type="button"
                  onClick={() => onLangChange("en")}
                  className={`rounded-sm px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider transition-colors ${
                    lang === "en" ? "text-white" : "text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                  }`}
                  style={lang === "en" ? { backgroundColor: BRAND_NAVY_LIGHT } : undefined}
                >
                  EN
                </button>
              </div>
            )}
            <button
              type="button"
              onClick={onClose}
              className="p-1.5 rounded text-gray-600 hover:bg-gray-200 hover:text-gray-800 transition-colors"
              aria-label={isEs ? "Cerrar" : "Close"}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Body: same structure as original policy page — big centered title, then intro, then numbered list */}
        <div className="overflow-y-auto flex-1 px-5 py-5 text-sm text-gray-800">
          <h3 className="text-xl font-bold text-gray-900 text-center mb-4">
            {bodyTitle}
          </h3>
          {policy === "terms" && (
            <>
              <p className="text-xs text-gray-500 tracking-wide mb-5 text-center">
                {isEs
                  ? "Al utilizar esta plataforma, el usuario acepta cumplir con las condiciones establecidas a continuación."
                  : "By using this platform, the user agrees to comply with the conditions set forth below."}
              </p>
              <ol className="divide-y divide-gray-100 text-left text-justify">
                <li className="py-3">
                  <p className="font-semibold mb-1">1. {isEs ? "Uso de la plataforma" : "Use of the Platform"}</p>
                  <p>
                    {isEs
                      ? "Esta plataforma permite a los usuarios realizar procesos de inscripción y pago para clases, actividades o programas ofrecidos por American School."
                      : "This platform allows users to complete enrollment and payment processes for classes, activities or programs offered by American School."}
                  </p>
                </li>
                <li className="py-3">
                  <p className="font-semibold mb-1">2. {isEs ? "Información proporcionada" : "Information Provided"}</p>
                  <p>
                    {isEs
                      ? "El usuario se compromete a proporcionar información veraz, completa y actualizada durante el proceso de inscripción, incluyendo los datos del estudiante y del responsable del pago."
                      : "The user agrees to provide truthful, complete and up-to-date information during the enrollment process, including student and payer details."}
                  </p>
                  <p className="mt-2">
                    {isEs
                      ? "American School no se hace responsable por inconvenientes derivados de información incorrecta o incompleta proporcionada por el usuario."
                      : "American School is not responsible for any issues arising from incorrect or incomplete information provided by the user."}
                  </p>
                </li>
                <li className="py-3">
                  <p className="font-semibold mb-1">3. {isEs ? "Confirmación de inscripción" : "Enrollment Confirmation"}</p>
                  <p>
                    {isEs
                      ? "La inscripción a una clase o actividad se considera confirmada únicamente cuando el pago correspondiente ha sido procesado correctamente y registrado en la plataforma."
                      : "Enrollment in a class or activity is considered confirmed only when the corresponding payment has been successfully processed and recorded on the platform."}
                  </p>
                </li>
                <li className="py-3">
                  <p className="font-semibold mb-1">4. {isEs ? "Modificaciones de servicios" : "Modifications of Services"}</p>
                  <p>
                    {isEs
                      ? "American School se reserva el derecho de modificar fechas, horarios o disponibilidad de clases por razones operativas o situaciones imprevistas. En dichos casos, se informará oportunamente a los usuarios registrados."
                      : "American School reserves the right to modify dates, schedules or class availability for operational reasons or unforeseen circumstances. In such cases, registered users will be informed in a timely manner."}
                  </p>
                </li>
                <li className="py-3">
                  <p className="font-semibold mb-1">5. {isEs ? "Uso adecuado" : "Proper Use"}</p>
                  <p>
                    {isEs
                      ? "El usuario se compromete a utilizar la plataforma de manera responsable y conforme a las leyes y regulaciones vigentes de la República Dominicana."
                      : "The user agrees to use the platform responsibly and in accordance with the laws and regulations in force in the Dominican Republic."}
                  </p>
                </li>
                <li className="py-3">
                  <p className="font-semibold mb-1">6. {isEs ? "Aceptación de los términos" : "Acceptance of the Terms"}</p>
                  <p>
                    {isEs
                      ? "Al realizar una inscripción o pago mediante esta plataforma, el usuario confirma haber leído y aceptado los presentes Términos de Uso."
                      : "By completing an enrollment or payment through this platform, the user confirms having read and accepted these Terms of Use."}
                  </p>
                </li>
              </ol>
            </>
          )}

          {policy === "refunds" && (
            <>
              <p className="text-xs text-gray-500 tracking-wide mb-5 text-center">
                {isEs
                  ? "La presente política establece las condiciones aplicables a pagos realizados a través de la plataforma de inscripción de American School."
                  : "This policy sets out the conditions applicable to payments made through American School's enrollment platform."}
              </p>
              <ol className="divide-y divide-gray-100 text-left text-justify">
                <li className="py-3">
                  <p className="font-semibold mb-1">1. {isEs ? "Pagos realizados" : "Payments Made"}</p>
                  <p>
                    {isEs
                      ? "Los pagos efectuados mediante esta plataforma corresponden a la inscripción del estudiante en clases o actividades ofrecidas por American School."
                      : "Payments made through this platform correspond to the student's enrollment in classes or activities offered by American School."}
                  </p>
                </li>
                <li className="py-3">
                  <p className="font-semibold mb-1">2. {isEs ? "Confirmación de cupo" : "Spot Confirmation"}</p>
                  <p>
                    {isEs
                      ? "Una vez realizado el pago y confirmada la inscripción, el cupo correspondiente en la actividad o clase seleccionada queda reservado para el estudiante."
                      : "Once payment has been made and enrollment confirmed, the corresponding spot in the selected activity or class is reserved for the student."}
                  </p>
                </li>
                <li className="py-3">
                  <p className="font-semibold mb-1">3. {isEs ? "Reembolsos" : "Refunds"}</p>
                  <p>
                    {isEs
                      ? "Los pagos realizados no son reembolsables una vez confirmada la inscripción, salvo en casos excepcionales determinados por American School."
                      : "Payments made are non-refundable once enrollment is confirmed, except in exceptional cases as determined by American School."}
                  </p>
                </li>
                <li className="py-3">
                  <p className="font-semibold mb-1">4. {isEs ? "Cancelaciones por parte de la institución" : "Cancellation by the Institution"}</p>
                  <p className="mb-2">
                    {isEs
                      ? "En caso de cancelación de una actividad por parte de American School, el usuario podrá optar por:"
                      : "If American School cancels an activity, the user may choose to:"}
                  </p>
                  <ul className="list-disc list-inside space-y-1">
                    <li>{isEs ? "Transferir el pago a otra actividad o clase disponible." : "Transfer the payment to another available activity or class."}</li>
                    <li>{isEs ? "Recibir un reembolso del monto pagado, según corresponda." : "Receive a refund of the amount paid, as applicable."}</li>
                  </ul>
                </li>
                <li className="py-3">
                  <p className="font-semibold mb-1">5. {isEs ? "Consultas" : "Inquiries"}</p>
                  <p>
                    {isEs
                      ? "Para solicitudes relacionadas con pagos, cancelaciones o modificaciones en las inscripciones, los usuarios pueden comunicarse con nuestro equipo de soporte a través del correo electrónico:"
                      : "For requests related to payments, cancellations or changes to enrollments, users may contact our support team at the following email address:"}
                  </p>
                  <p className="mt-2">
                    <a href="mailto:info@americanschool.edu.do" className="text-[#5856d6] hover:text-[#3b3ab8] transition-colors">
                      info@americanschool.edu.do
                    </a>
                  </p>
                </li>
              </ol>
            </>
          )}

          {policy === "privacy" && (
            <>
              <p className="text-xs text-gray-500 tracking-wide mb-5 text-center">
                {isEs
                  ? "American School se compromete a proteger la privacidad y la información personal de los usuarios que utilizan esta plataforma."
                  : "American School is committed to protecting the privacy and personal information of users of this platform."}
              </p>
              <ol className="divide-y divide-gray-100 text-left text-justify">
                <li className="py-3">
                  <p className="font-semibold mb-1">1. {isEs ? "Información recopilada" : "Information Collected"}</p>
                  <p className="mb-2">
                    {isEs
                      ? "Durante el proceso de inscripción y uso de la plataforma, podemos recopilar información personal como:"
                      : "During the enrollment process and use of the platform, we may collect personal information such as:"}
                  </p>
                  <ul className="list-disc list-inside space-y-1">
                    <li>{isEs ? "Nombre del estudiante" : "Student's name"}</li>
                    <li>{isEs ? "Datos de contacto del responsable" : "Contact details of the responsible party"}</li>
                    <li>{isEs ? "Información necesaria para la facturación cuando se solicita comprobante fiscal" : "Information required for billing when a fiscal receipt is requested"}</li>
                  </ul>
                </li>
                <li className="py-3">
                  <p className="font-semibold mb-1">2. {isEs ? "Uso de la información" : "Use of Information"}</p>
                  <p className="mb-2">
                    {isEs ? "La información recopilada se utiliza exclusivamente para:" : "The information collected is used exclusively for:"}
                  </p>
                  <ul className="list-disc list-inside space-y-1">
                    <li>{isEs ? "Gestionar inscripciones y registros de estudiantes" : "Managing enrollments and student records"}</li>
                    <li>{isEs ? "Procesar pagos correspondientes a las actividades ofrecidas" : "Processing payments for the activities offered"}</li>
                    <li>{isEs ? "Comunicarnos con los usuarios respecto a sus inscripciones o servicios relacionados" : "Communicating with users regarding their enrollments or related services"}</li>
                  </ul>
                </li>
                <li className="py-3">
                  <p className="font-semibold mb-1">3. {isEs ? "Seguridad de los pagos" : "Payment Security"}</p>
                  <p>
                    {isEs
                      ? "Los pagos realizados a través de esta plataforma son procesados mediante la pasarela de pago segura AZUL."
                      : "Payments made through this platform are processed via the secure AZUL payment gateway."}
                  </p>
                  <p className="mt-2">
                    {isEs
                      ? "American School no almacena ni tiene acceso a los datos completos de tarjetas de crédito o débito utilizados durante el proceso de pago."
                      : "American School does not store nor have access to the full credit or debit card data used during the payment process."}
                  </p>
                </li>
                <li className="py-3">
                  <p className="font-semibold mb-1">4. {isEs ? "Protección de datos" : "Data Protection"}</p>
                  <p>
                    {isEs
                      ? "American School adopta medidas razonables de seguridad para proteger la información personal contra accesos no autorizados, uso indebido o divulgación."
                      : "American School adopts reasonable security measures to protect personal information against unauthorized access, misuse or disclosure."}
                  </p>
                </li>
                <li className="py-3">
                  <p className="font-semibold mb-1">5. {isEs ? "Contacto" : "Contact"}</p>
                  <p>
                    {isEs
                      ? "Para cualquier consulta relacionada con esta política o con el manejo de datos personales, los usuarios pueden contactarnos a través del correo electrónico:"
                      : "For any inquiry related to this policy or the handling of personal data, users may contact us at the following email address:"}
                  </p>
                  <p className="mt-2">
                    <a href="mailto:info@americanschool.edu.do" className="text-[#5856d6] hover:text-[#3b3ab8] transition-colors">
                      info@americanschool.edu.do
                    </a>
                  </p>
                </li>
              </ol>
            </>
          )}
        </div>

        {/* Footer: Cerrar button bottom right */}
        <div className="border-t border-gray-200 bg-gray-50 px-4 py-3 flex justify-end shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2 rounded-lg bg-gray-700 text-white text-sm font-medium hover:bg-gray-800 transition-colors"
          >
            {isEs ? "Cerrar" : "Close"}
          </button>
        </div>
      </div>
    </div>
  );
}
